/**
 * Plan limit helpers.
 *
 * Plans:
 *   trial        — default after registration, no Stripe subscription (status null / canceled)
 *   starter      — 29 €/mo, Stripe status 'trialing' or 'active', plan='starter'
 *   professional — 79 €/mo, plan='professional'
 *   enterprise   — 199 €/mo, plan='enterprise'
 *
 * Property limits:
 *   trial        → 1
 *   starter      → 10
 *   professional → 100
 *   enterprise   → Infinity
 *
 * Tenant limits:
 *   trial        → 1
 *   all paid     → Infinity
 */

/** True when the user has an active (or trialing) paid subscription. */
export function isPro(subscription) {
  const s = subscription?.status;
  return s === 'trialing' || s === 'active';
}

/** The effective plan key: 'trial' | 'starter' | 'professional' | 'enterprise' */
export function getEffectivePlan(subscription) {
  if (!isPro(subscription)) return 'trial';
  return subscription?.plan || 'starter';
}

export function getPropertyLimit(subscription) {
  const plan = getEffectivePlan(subscription);
  if (plan === 'enterprise')   return Infinity;
  if (plan === 'professional') return 100;
  if (plan === 'starter')      return 10;
  return 1; // trial
}

export function getTenantLimit(subscription) {
  if (!isPro(subscription)) return 1; // trial
  return Infinity;
}

export function canCreateProperty(subscription, currentCount) {
  return currentCount < getPropertyLimit(subscription);
}

export function canCreateTenant(subscription, currentCount) {
  return currentCount < getTenantLimit(subscription);
}

// Named alias used by backend-style checks
export function checkPropertyLimit(subscription, currentCount) {
  return canCreateProperty(subscription, currentCount);
}

export function checkTenantLimit(subscription, currentCount) {
  return canCreateTenant(subscription, currentCount);
}

// Future: document limits (structure ready)
export function getDocumentLimit(subscription) {
  if (!isPro(subscription)) return 5;
  const plan = getEffectivePlan(subscription);
  if (plan === 'enterprise')   return Infinity;
  if (plan === 'professional') return Infinity;
  return 50; // starter
}

export function canCreateDocument(subscription, currentCount) {
  return currentCount < getDocumentLimit(subscription);
}

/** Usage string for UI: e.g. "3 / 10 Objekte" */
export function getUsageLabel(subscription, currentCount, type = 'properties') {
  const limit = type === 'properties' ? getPropertyLimit(subscription)
              : type === 'tenants'    ? getTenantLimit(subscription)
              : getDocumentLimit(subscription);
  const noun  = type === 'properties' ? 'Objekte'
              : type === 'tenants'    ? 'Mieter'
              : 'Dokumente';
  if (limit === Infinity) return `${currentCount} ${noun} (unbegrenzt)`;
  return `${currentCount} / ${limit} ${noun}`;
}

/** Human-readable plan name for UI */
export function getPlanLabel(subscription) {
  const plan = getEffectivePlan(subscription);
  const labels = {
    trial:        'Test-Phase',
    starter:      'Starter',
    professional: 'Professional',
    enterprise:   'Enterprise',
  };
  return labels[plan] || 'Test-Phase';
}

/** Badge color class based on plan */
export function getPlanColor(subscription) {
  const plan = getEffectivePlan(subscription);
  if (plan === 'enterprise')   return { bg: '#fef3c7', border: '#fde68a', color: '#92400e' };
  if (plan === 'professional') return { bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d' };
  if (plan === 'starter')      return { bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d' };
  return { bg: '#f8fafc', border: '#e5e7eb', color: '#6b7280' }; // trial
}
